import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertModule,
  BadgeComponent,
  ButtonCloseDirective,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  ModalModule,
  RowComponent,
  SpinnerComponent
} from '@coreui/angular-pro';
import {
  cilArrowLeft,
  cilCloudDownload,
  cilCopy,
  cilExternalLink,
  cilFile,
  cilInfo
} from '@coreui/icons';
import { IconDirective, IconModule, IconSetService } from '@coreui/icons-angular';
import { I18nService, TranslatePipe } from 'i18n';
import { DownloadService, Logger } from 'shared';

interface Order {
  id: number;
  payment_method: string;
  status: string;
  total_price: string;
  payment_costs?: string;
  grand_total?: string;
  currency?: string;
  items_count: number;
  created_at: string;
  updated_at: string;
  web3_transaction_hash?: string;
  web3_confirmations?: number;
  web3_verified_at?: string;
  order_details?: OrderDetail[];
}

interface OrderDetail {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: string;
  subtotal: string;
}

@Component({
  selector: 'app-order-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    AlertModule,
    BadgeComponent,
    ButtonDirective,
    ButtonCloseDirective,
    CardBodyComponent,
    CardComponent,
    CardHeaderComponent,
    ColComponent,
    ContainerComponent,
    ModalModule,
    RowComponent,
    IconDirective,
    IconModule,
    TranslatePipe,
    SpinnerComponent
  ],
  templateUrl: './order-detail-page.component.html',
  styleUrls: ['./order-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderDetailPageComponent {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private i18nService = inject(I18nService);
  private downloadService = inject(DownloadService);
  private iconSet = inject(IconSetService);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  protected logger = new Logger('OrderDetailPageComponent');

  readonly parseFloat = parseFloat;
  readonly parseInt = parseInt;
  readonly Math = Math;
  readonly Number = Number;

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  order = signal<Order | null>(null);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);
  currentLocale = signal<string>('en-US');

  billPreviewModalVisible = signal<boolean>(false);
  billPreviewUrl = signal<SafeResourceUrl | null>(null);

  // Computed
  grandTotal = computed(() => {
    const ord = this.order();
    if (!ord) return 0;
    const total = parseFloat(ord.total_price) || 0;
    const costs = ord.payment_costs ? parseFloat(ord.payment_costs) : 0;
    return total + costs;
  });

  statusBadgeColor = computed(() => {
    const status = this.order()?.status || '';
    const map: Record<string, string> = {
      completed: 'success',
      processing: 'info',
      awaiting_confirmation: 'warning',
      awaiting_connection: 'warning',
      wallet_connected: 'info',
      pending: 'secondary',
      failed: 'danger',
      cancelled: 'dark'
    };
    return map[status] || 'secondary';
  });

  formattedStatus = computed(() => {
    const status = this.order()?.status || '';
    const map: Record<string, string> = {
      pending: 'Pending',
      awaiting_confirmation: 'Awaiting Confirmation',
      wallet_connected: 'Wallet Connected',
      awaiting_connection: 'Awaiting Connection',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return map[status] || status;
  });

  hasPaymentCosts = computed(() => {
    const costs = this.order()?.payment_costs;
    return costs ? parseFloat(costs) > 0 : false;
  });
  
  paymentCosts = computed(() => {
    const costs = this.order()?.payment_costs;
    return costs ? parseFloat(costs) : 0;
  });

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    this.iconSet.icons = {
      ...this.iconSet.icons,
      cilArrowLeft,
      cilCopy,
      cilExternalLink,
      cilCloudDownload,
      cilFile,
      cilInfo
    };

    // React to locale changes
    effect(() => {
      const lang = this.i18nService.getLanguage();
      const localeMap: Record<string, string> = {
        en: 'en-US',
        de: 'de-DE',
        es: 'es-ES',
        fr: 'fr-FR',
        pt: 'pt-PT',
        it: 'it-IT'
      };
      const langCode = lang.split('-')[0];
      this.currentLocale.set(localeMap[langCode] || 'en-US');
    });

    // Load order on init
    effect(() => {
      const orderId = this.route.snapshot.paramMap.get('id');
      if (orderId) {
        this.loadOrder(parseInt(orderId, 10));
      } else {
        this.error.set('No order ID provided');
        this.loading.set(false);
      }
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private loadOrder(orderId: number): void {
    this.loading.set(true);
    this.error.set(null);

    this.http.get<Order>(`/orders/${orderId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: order => {
          this.order.set(order);
          this.loading.set(false);
          this.logger.debug('Order loaded:', order.id);
        },
        error: err => {
          this.logger.error('Failed to load order', err);
          this.error.set('Failed to load order details');
          this.loading.set(false);
        }
      });
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(this.currentLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number | string | null | undefined): string {
    // USDT amounts are displayed as USD (1:1)
    const rawCode = this.order()?.currency || 'USDT';
    const code = rawCode.toUpperCase() === 'USDT' ? 'USD' : rawCode;
    if (amount == null) return `0.00 ${code}`;

    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return `0.00 ${code}`;

    const formatted = new Intl.NumberFormat(this.currentLocale(), {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);

    const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[code.toUpperCase()];
    return symbol ? `${symbol}${formatted}` : `${formatted} ${code}`;
  }

  formatPaymentMethod(method: string): string {
    const map: Record<string, string> = {
      ewallet: 'eWallet',
      web3_payment: 'Web3/Crypto',
      credit_card: 'Credit Card',
      direct_fiat: 'Direct Fiat Transfer'
    };
    return map[method] || method;
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text)
      .then(() => this.logger.debug('Copied to clipboard:', text))
      .catch(err => {
        this.logger.error('Failed to copy', err);
        alert('Failed to copy to clipboard');
      });
  }

  downloadBill(orderId: number): void {
    const locale = this.i18nService.getLanguage();
    const filename = `Invoice_${orderId}_${new Date().toISOString().split('T')[0]}.pdf`;

    this.downloadService
      .download(`/orders/${orderId}/bill?locale=${locale}`,
        this.elementRef.nativeElement,
        {
          filename,
          open: true,
          successMessage: `Invoice downloaded for order ${orderId}`,
          errorMessage: 'Failed to download invoice. Please try again.'
        }
      )
      .then(() => this.logger.debug(`Invoice downloaded for order ${orderId}`))
      .catch(err => {
        this.logger.error('Bill download failed', err);
      });
  }

  previewBill(orderId: number): void {
    const locale = this.i18nService.getLanguage();

    this.http.get(`/orders/${orderId}/bill/preview?locale=${locale}`, {
      responseType: 'blob',
      observe: 'response'
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.logger.error('No PDF data for preview');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        this.billPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
        this.billPreviewModalVisible.set(true);
      },
      error: err => {
        this.logger.error('Bill preview failed', err);
      }
    });
  }

  closeBillPreview(): void {
    const current = this.billPreviewUrl();
    if (current) {
      const url = (current as any).changingThisBreaksApplicationSecurity;
      if (url?.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    }
    this.billPreviewModalVisible.set(false);
    this.billPreviewUrl.set(null);
  }
}